/**
 * Deriving "is the recording actually REACHING the server?" from the chunk
 * queue, for the student- and proctor-facing indicators.
 *
 * Today's real failure was SILENT: recording produced zero uploaded chunks for
 * 40 minutes and nobody knew. This turns the queue's own numbers into a state
 * the UI can show loudly:
 *
 *   ok           — nothing queued, or a small backlog that is draining
 *   behind       — a real backlog that is still moving (uplink is slow)
 *   stalled      — a backlog with NO successful upload for a while (or none
 *                  ever): footage is not reaching the server
 *   not-recording — worse than stalled: nothing is being PRODUCED, so there is
 *                  no backlog at all and every queue number reads perfect
 *
 * That last state is the one this file originally could not see. Health was
 * derived purely from `pending`, and a recorder that stops producing has an
 * empty queue — indistinguishable, from here, from a recorder that is calmly
 * keeping up. So it must be told about production explicitly, which is what
 * `notProducing` is for.
 */
import { useEffect, useRef, useState } from "react";
import type { UploaderStatus } from "./uploader";

/** No acknowledged upload in this long, with a backlog, means it is not landing. */
const STALL_MS = 45_000;
/** A backlog at least this deep (still moving) is "behind". */
const BEHIND_PENDING = 3;

export type UploadHealthState =
  | "ok"
  | "behind"
  | "stalled"
  | "not-recording";

export interface UploadHealth {
  state: UploadHealthState;
  pending: number;
  pendingBytes: number;
  uploaded: number;
  /** Times the uploader wedged and had to be forcibly restarted. */
  wedgeRecoveries: number;
}

/** Merge several sources' upload statuses (e.g. webcam + screen) into one view. */
export function combineUploadStatus(
  statuses: UploaderStatus[],
): UploaderStatus {
  return statuses.reduce<UploaderStatus>(
    (acc, s) => ({
      pending: acc.pending + s.pending,
      pendingBytes: acc.pendingBytes + s.pendingBytes,
      uploading: acc.uploading || s.uploading,
      failures: Math.max(acc.failures, s.failures),
      uploaded: acc.uploaded + s.uploaded,
      refused: acc.refused + s.refused,
      lastErrorMessage: s.lastErrorMessage ?? acc.lastErrorMessage,
      lastUploadAtMs: Math.max(acc.lastUploadAtMs ?? 0, s.lastUploadAtMs ?? 0) ||
        null,
      wedgeRecoveries: acc.wedgeRecoveries + s.wedgeRecoveries,
    }),
    {
      pending: 0,
      pendingBytes: 0,
      uploading: false,
      failures: 0,
      uploaded: 0,
      refused: 0,
      lastErrorMessage: null,
      lastUploadAtMs: null,
      wedgeRecoveries: 0,
    },
  );
}

export function useUploadHealth(
  upload: UploaderStatus,
  /** True when a recorder is producing no video despite being told to. */
  notProducing = false,
): UploadHealth {
  // Re-evaluate on a timer: "stalled" is a function of elapsed wall time since
  // the last success, which changes even when `upload` does not.
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 5_000);
    return () => clearInterval(id);
  }, []);

  // When did the current never-uploaded backlog begin? Guards the startup window
  // (the first chunk is queued ~10s before its first upload) from false alarms.
  const firstBacklogAtRef = useRef<number | null>(null);
  const now = Date.now();
  if (upload.pending > 0 && upload.uploaded === 0) {
    if (firstBacklogAtRef.current === null) firstBacklogAtRef.current = now;
  } else {
    firstBacklogAtRef.current = null;
  }

  let state: UploadHealthState = "ok";
  // Ranked first: no footage at all beats a backlog of footage that is merely
  // late, and it is the state the student most needs to act on.
  if (notProducing) state = "not-recording";
  else if (upload.pending > 0) {
    const neverLandedStalled =
      upload.uploaded === 0 &&
      firstBacklogAtRef.current !== null &&
      now - firstBacklogAtRef.current > STALL_MS;
    const wentQuietStalled =
      upload.lastUploadAtMs !== null && now - upload.lastUploadAtMs > STALL_MS;

    if (neverLandedStalled || wentQuietStalled) state = "stalled";
    else if (upload.pending >= BEHIND_PENDING) state = "behind";
  }

  return {
    state,
    pending: upload.pending,
    pendingBytes: upload.pendingBytes,
    uploaded: upload.uploaded,
    wedgeRecoveries: upload.wedgeRecoveries,
  };
}

export function formatMB(bytes: number): string {
  return `${(bytes / 1_000_000).toFixed(1)} MB`;
}
